"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemesQuickVoteQueue, type UseMemesQuickVoteQueueResult } from "@/hooks/useMemesQuickVoteQueue";
import { useCallback, useRef, useState } from "react";
export type MemesQuickVoteDialogState = Pick<UseMemesQuickVoteQueueResult, "activeDrop" | "hasDiscoveryError" | "isExhausted" | "latestUsedAmount" | "nextDrop" | "recentAmounts" | "remainingCount" | "retryDiscovery" | "submitVote" | "skipDrop" | "uncastPower" | "votingLabel"> & {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly sessionId: number;
};
type UseMemesQuickVoteDialogControllerResult = {
  readonly closeQuickVote: () => void;
  readonly dialogState: MemesQuickVoteDialogState;
  readonly isQuickVoteOpen: boolean;
  readonly openQuickVote: () => void;
  readonly prefetchQuickVote: () => void;
  readonly quickVoteSessionId: number;
};
export const useMemesQuickVoteDialogController = () => {
  const $ = _c(32);
  const [isQuickVoteOpen, setIsQuickVoteOpen] = useState(false);
  const [quickVoteSessionId, setQuickVoteSessionId] = useState(0);
  const lastIssuedSessionIdRef = useRef(0);
  const reservedSessionIdRef = useRef(null);
  const t0 = quickVoteSessionId > 0;
  let t1;
  if ($[0] !== quickVoteSessionId || $[1] !== t0) {
    t1 = {
      enabled: t0,
      sessionId: quickVoteSessionId
    };
    $[0] = quickVoteSessionId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const quickVoteQueue = useMemesQuickVoteQueue(t1);
  const {
    retryDiscovery
  } = quickVoteQueue;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (reservedSessionIdRef.current !== null) {
        return reservedSessionIdRef.current;
      }
      const nextSessionId = lastIssuedSessionIdRef.current + 1;
      reservedSessionIdRef.current = nextSessionId;
      return nextSessionId;
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const reserveSessionId = t2;
  let t3;
  if ($[4] !== quickVoteSessionId || $[5] !== retryDiscovery) {
    t3 = () => {
      if (quickVoteSessionId > 0) {
        retryDiscovery();
        return;
      }
      const sessionId = reserveSessionId();
      lastIssuedSessionIdRef.current = sessionId;
      reservedSessionIdRef.current = null;
      setQuickVoteSessionId(sessionId);
    };
    $[4] = quickVoteSessionId;
    $[5] = retryDiscovery;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const prefetchQuickVote = t3;
  let t4;
  if ($[7] !== quickVoteSessionId || $[8] !== retryDiscovery) {
    t4 = () => {
      if (quickVoteSessionId > 0) {
        retryDiscovery();
        setIsQuickVoteOpen(true);
        return;
      }
      const sessionId_0 = reservedSessionIdRef.current ?? lastIssuedSessionIdRef.current + 1;
      lastIssuedSessionIdRef.current = sessionId_0;
      reservedSessionIdRef.current = null;
      setQuickVoteSessionId(sessionId_0);
      setIsQuickVoteOpen(true);
    };
    $[7] = quickVoteSessionId;
    $[8] = retryDiscovery;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const openQuickVote = t4;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsQuickVoteOpen(false);
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const closeQuickVote = t5;
  let t6;
  if ($[11] !== isQuickVoteOpen || $[12] !== quickVoteQueue.activeDrop || $[13] !== quickVoteQueue.hasDiscoveryError || $[14] !== quickVoteQueue.isExhausted || $[15] !== quickVoteQueue.latestUsedAmount || $[16] !== quickVoteQueue.nextDrop || $[17] !== quickVoteQueue.recentAmounts || $[18] !== quickVoteQueue.remainingCount || $[19] !== quickVoteQueue.retryDiscovery || $[20] !== quickVoteQueue.skipDrop || $[21] !== quickVoteQueue.submitVote || $[22] !== quickVoteQueue.uncastPower || $[23] !== quickVoteQueue.votingLabel || $[24] !== quickVoteSessionId) {
    t6 = {
      activeDrop: quickVoteQueue.activeDrop,
      hasDiscoveryError: quickVoteQueue.hasDiscoveryError,
      isExhausted: quickVoteQueue.isExhausted,
      isOpen: isQuickVoteOpen,
      latestUsedAmount: quickVoteQueue.latestUsedAmount,
      nextDrop: quickVoteQueue.nextDrop,
      onClose: closeQuickVote,
      recentAmounts: quickVoteQueue.recentAmounts,
      remainingCount: quickVoteQueue.remainingCount,
      retryDiscovery: quickVoteQueue.retryDiscovery,
      sessionId: quickVoteSessionId,
      skipDrop: quickVoteQueue.skipDrop,
      submitVote: quickVoteQueue.submitVote,
      uncastPower: quickVoteQueue.uncastPower,
      votingLabel: quickVoteQueue.votingLabel
    };
    $[11] = isQuickVoteOpen;
    $[12] = quickVoteQueue.activeDrop;
    $[13] = quickVoteQueue.hasDiscoveryError;
    $[14] = quickVoteQueue.isExhausted;
    $[15] = quickVoteQueue.latestUsedAmount;
    $[16] = quickVoteQueue.nextDrop;
    $[17] = quickVoteQueue.recentAmounts;
    $[18] = quickVoteQueue.remainingCount;
    $[19] = quickVoteQueue.retryDiscovery;
    $[20] = quickVoteQueue.skipDrop;
    $[21] = quickVoteQueue.submitVote;
    $[22] = quickVoteQueue.uncastPower;
    $[23] = quickVoteQueue.votingLabel;
    $[24] = quickVoteSessionId;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== isQuickVoteOpen || $[27] !== openQuickVote || $[28] !== prefetchQuickVote || $[29] !== quickVoteSessionId || $[30] !== t6) {
    t7 = {
      closeQuickVote,
      dialogState: t6,
      isQuickVoteOpen,
      openQuickVote,
      prefetchQuickVote,
      quickVoteSessionId
    };
    $[26] = isQuickVoteOpen;
    $[27] = openQuickVote;
    $[28] = prefetchQuickVote;
    $[29] = quickVoteSessionId;
    $[30] = t6;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  return t7;
};